import Vue from 'vue'
import Vuex from 'vuex'
import { getInstance } from '@/auth/auth0-plugin'
import { formatParty } from '@/config/party-formatting'

// API
import DocumentTag from '@/axios/document-tag'
import Party from '@/axios/party'
import Search from '@/axios/search'

Vue.use(Vuex)

export default new Vuex.Store({
  state: {
    root_key: 'ipro_portal',
    showDefaultView: true,
    statuses: [
      { id: 'CURRENT', name: 'Current' },
      { id: 'ARCHIVE', name: 'Archive' }
    ],
    doc_tags: [],
    userPartyId: null,
    userRelatedParties: [],
    activeUserRelatedParties: [],
    ownerRetailerAdGroups: [],
    retailCategoryIds: ['RETAILER_REPORT'],
    vendorCategoryIds: ['VENDOR_REPORT'],
    adminCategoryIds: ['ADMIN_REPORT'],
    adGroups: [],
    tenantStores: [],
    allRetailersParty: null,
    dealFileOwnerGroupCodes: [
      '342', '404', '369', '290', '270', '477', '089', '316',
      '348', '276', '252', '505', '669', '618', '637'
    ]
  },
  getters: {
    showDefaultView (state) {
      return state.showDefaultView
    },
    root_key (state) {
      return state.root_key
    },
    doc_tags (state) {
      return state.doc_tags
    },
    email () {
      const authService = getInstance()
      if (authService.isAuthenticated) {
        return authService.user.email
      }
      return ''
    },
    categories (state) {
      const tag = state.doc_tags.find(t => t?.id === 'CATEGORY')
      return tag?.children || []
    },
    retailCategories (state, getters) {
      return getters.categories.filter(c => state.retailCategoryIds.includes(c?.id))
    },
    vendorCategories (state, getters) {
      return getters.categories.filter(c => state.vendorCategoryIds.includes(c?.id))
    },
    adminCategories (state, getters) {
      return getters.categories.filter(c => state.adminCategoryIds.includes(c?.id))
    },
    statuses (state) {
      return state.statuses
    },
    userPartyId (state) {
      return state.userPartyId
    },
    userRelatedParties (state) {
      return state.userRelatedParties
    },
    activeUserRelatedParties (state) {
      return state.activeUserRelatedParties
    },
    ownerRetailerAdGroups (state) {
      return state.ownerRetailerAdGroups
    },
    userRelatedPartyIds (state) {
      if (state.userRelatedParties.length > 0) {
        return state.userRelatedParties.map(party => party.id)
      }
      return []
    },
    activeUserRelatedPartyIds (state) {
      if (state.activeUserRelatedParties.length > 0) {
        return state.activeUserRelatedParties.map(party => party.id)
      }
      return []
    },
    ownerRetailerAdGroupIds (state) {
      if (state.ownerRetailerAdGroups.length > 0) {
        return state.ownerRetailerAdGroups.map(party => party.id)
      }
      return []
    },
    adGroups (state) {
      return state.adGroups
    },
    tenantStores (state) {
      return state.tenantStores
    },
    allRetailersParty (state) {
      return state.allRetailersParty
    },
    dealFileOwnerGroupCodes (state) {
      return state.dealFileOwnerGroupCodes
    }
  },
  mutations: {
    setShowDefaultView (state, value) {
      state.showDefaultView = value
    },
    setDocumentTags (state, payload) {
      state.doc_tags = payload
    },
    setUserPartyId (state, payload) {
      state.userPartyId = payload
    },
    setUserRelatedParties (state, payload) {
      state.userRelatedParties = payload
    },
    setActiveUserRelatedParties (state, payload) {
      state.activeUserRelatedParties = payload
    },
    setOwnerRetailerAdGroups (state, payload) {
      state.ownerRetailerAdGroups = payload
    },
    setAdGroups (state, payload) {
      state.adGroups = payload
    },
    setTenantStores (state, payload) {
      state.tenantStores = payload
    },
    setAllRetailersParty (state, payload) {
      state.allRetailersParty = payload
    }
  },
  actions: {
    async getDocumentTags (context) {
      if (context.getters.doc_tags.length === 0) {
        try {
          const res = await DocumentTag.getList()
          context.commit('setDocumentTags', res.data)
        } catch (err) {
          console.error("Could not retrieve document tags.", err)
          throw err
        }
      }
    },
    async getUserRelatedParties (context) {
      const userParties = context.getters.userRelatedParties
      if (userParties.length > 0) return

      const authService = getInstance()
      const { tenant, permissions, userRoles, user: { email } } = authService
      const include_stores = userRoles.some(roleName => {
        return roleName?.toLowerCase()?.includes('retail')
          || ['promo.coordinator', 'pce-owner', 'arg-group-manager'].includes(roleName)
      })
      const limit_stores = permissions.includes('retail:related_stores')
      const limit_subvendors = (tenant === 'alliance-retail-group') && permissions?.includes('promo:related_subvendors')

      const owners_from_children = userRoles.some(roleName => ['promo.coordinator', 'arg-store-retailer', 'arg-group-manager', 'arg-owner-retailer'].includes(roleName))
      const extended_access = include_stores // same criteria, but new var for readability

      let extendedRelatedParties = []
      let relatedParties = []
      try {
        const [res, extended_res] = await Promise.allSettled([
          Party.getUserProfile({ 
            email,
            include_stores, 
            limit_stores, 
            owners_from_children, 
            limit_subvendors 
          }),
          Party.getUserProfile({
            email,
            include_stores, 
            limit_stores, 
            owners_from_children, 
            limit_subvendors,
            extended_access 
          })
        ])

        if (res?.value?.data?.length > 0 || extended_res?.value?.data?.length > 0) {
          relatedParties = res.value.data.map(p => formatParty(p))
          extendedRelatedParties = extended_res.value.data.map(p => formatParty(p))
        }
        if (tenant === 'alliance-retail-group' && userRoles.includes('arg-vendor')) {
          const allVendorsParty = await Party.getAllVendorsParty()
          if (allVendorsParty) {
            extendedRelatedParties.push({ 
              ...allVendorsParty,
              party_type_name: 'Vendor',
              party_type_constant: 'VENDOR'
            })
          }
        }
        if (tenant === 'alliance-retail-group' && include_stores) {
          const allRetailersParty = await Party.getAllRetailersParty()
          if (allRetailersParty) {
            context.commit('setAllRetailersParty', allRetailersParty)
            
            extendedRelatedParties.push({ 
              ...allRetailersParty,
              party_type_name: 'Store',
              party_type_constant: 'STORE'
            })
            relatedParties.push({ 
              ...allRetailersParty,
              party_type_name: 'Store',
              party_type_constant: 'STORE'
            })
          }
        }
        
        // For arg-owner-retailer, fetch AD_GROUPs for the stores they have access to
        const ownerRetailerAdGroups = []
        if (tenant === 'alliance-retail-group' && userRoles.includes('arg-owner-retailer')) {
          const stores = extendedRelatedParties.filter(p => p.party_type_constant === 'STORE')
          const storeIds = stores.map(store => store.id)
          const adGroupIds = new Set()
          
          // Fetch AD_GROUPs for all stores in a single batch call
          try {
            const res = await Party.getUpstreamPartiesBatch(storeIds, ['AD_GROUP'], true)
            if (res?.data?.related?.length > 0) {
              res.data.related.forEach(id => adGroupIds.add(id))
            }
          } catch (err) {
            console.error('Error fetching AD_GROUPs for stores:', err)
          }
          
          // Fetch full party details for each AD_GROUP and store separately
          await Promise.allSettled(
            Array.from(adGroupIds).map(async (adGroupId) => {
              try {
                const res = await Party.get(adGroupId)
                if (res?.data && res.data.party_type?.constant === 'AD_GROUP') {
                  ownerRetailerAdGroups.push({
                    id: res.data.id,
                    name: res.data.name,
                    party_type_constant: 'AD_GROUP',
                    party_type_name: 'Ad Group'
                  })
                }
              } catch (err) {
                console.error(`Error fetching AD_GROUP ${adGroupId}:`, err)
              }
            })
          )
          
          // Store AD groups separately
          context.commit('setOwnerRetailerAdGroups', ownerRetailerAdGroups)
        }
        
        context.commit('setActiveUserRelatedParties', relatedParties)
        return context.commit('setUserRelatedParties', extendedRelatedParties)
      } catch (err) {
        console.error(err)
      }
    },
    async getAdGroups (context) {
      if (context.getters.adGroups.length > 0) return
      let adGroups = []
      try {
        const { data } = await Search.customerSearch('AD_GROUP')
        if (data?.length > 0) {
          adGroups = data.flatMap(party => {
            if (party.party_type === 'AD_GROUP') {
              return { ...party, id: party.party_id, name: party.party_name }
            }
            return []
          })
        }
        return context.commit('setAdGroups', adGroups)
      } catch (err) {
        console.error(err)
      }
    },
    async getTenantStores (context) {
      let stores = []
      const authService = getInstance()
      if (authService.tenant !== 'pricechopper') return
      try {
        const { data } = await Party.getAllStoresForTenant()
        if (data?.length > 0) {
          stores = data.flatMap(store => {
            return { ...store, id: store.store_party_id, name: `#${store.wholesaler_store_number || ''} ${store.store_name}` }
          })
        }
        return context.commit('setTenantStores', stores)
      } catch (err) {
        console.error(err)
      }
    }
  }
})
